import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, Eye, Calendar, User, Video } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import * as educationService from '../services/educationService';
import type { EducationArticle } from '../services/educationService';
import { getAbsoluteImageUrl } from '../services/educationService';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<EducationArticle | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      loadArticle();
    }
  }, [slug]);

  const loadArticle = async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const data = await educationService.getArticle(slug);
      setArticle(data);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeId = (url: string): string | null => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Articol negăsit</h2>
          <button
            onClick={() => navigate('/education')}
            className="text-purple-600 hover:text-purple-700"
          >
            Înapoi la Educație
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <PageHeader 
        title={article.title}
        subtitle={article.category?.name || 'Articol'}
        showBack={true}
        backTo="/education"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Article Card */}
        <article className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* YouTube Video Player */}
          {article.type === 'VIDEO' && article.videoUrl && getYouTubeId(article.videoUrl) ? (
            <div className="aspect-video bg-black">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${getYouTubeId(article.videoUrl)}`}
                title={article.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          ) : article.imageUrl ? (
            /* Featured Image */
            <div className="aspect-video bg-gray-100">
              <img
                src={getAbsoluteImageUrl(article.imageUrl)}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          ) : null}

          <div className="p-8">
            {/* Video Badge */}
            {article.type === 'VIDEO' && (
              <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg">
                <Video className="w-5 h-5" />
                <span className="font-semibold">Video Ghid</span>
              </div>
            )}

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
              {article.authorName && (
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span>{article.authorName}</span>
                  {article.authorTitle && <span className="text-gray-400">• {article.authorTitle}</span>}
                </div>
              )}
              {article.readTime && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{article.readTime} min citire</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{article.viewCount} vizualizări</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{new Date(article.publishedAt || article.createdAt).toLocaleDateString('ro-RO')}</span>
              </div>
            </div>

            {/* Excerpt */}
            {article.excerpt && (
              <div className="text-lg text-gray-700 mb-6 pb-6 border-b border-gray-200">
                {article.excerpt}
              </div>
            )}

            {/* Content */}
            <div className="prose prose-purple max-w-none">
              <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                {article.content}
              </div>
            </div>

            {/* Tags */}
            {article.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        {/* Related Articles placeholder */}
      </div>
    </div>
  );
}
